import { useCallback, useState } from 'react';

import { usePostHogCapture } from '@/hooks/usePostHogCapture';

const FAVORITED_MATTERS_STORAGE_KEY = 'finch-favorited-matters-v1';
const MAX_FAVORITED_MATTERS = 100;

export interface FavoritedMatterData {
  matterId: string;
  matterName: string;
  favoritedAt: string;
}

type FavoritedMattersMap = Record<string, FavoritedMatterData>;

/**
 * Load favorited matters from localStorage
 */
const loadFromStorage = (): FavoritedMattersMap => {
  try {
    const stored = localStorage.getItem(FAVORITED_MATTERS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (typeof parsed === 'object' && parsed !== null) {
        return parsed as FavoritedMattersMap;
      }
    }
  } catch (error) {
    console.error('Failed to load favorited matters from localStorage:', error);
  }
  return {};
};

/**
 * Save favorited matters to localStorage
 */
const saveToStorage = (data: FavoritedMattersMap): void => {
  try {
    localStorage.setItem(FAVORITED_MATTERS_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save favorited matters to localStorage:', error);
  }
};

/**
 * Hook for managing favorited/starred matters in localStorage.
 * Provides a simple API to favorite, unfavorite, and check if a matter is favorited.
 */
export const useFavoritedMatters = () => {
  const [favoritedMatters, setFavoritedMatters] = useState<FavoritedMattersMap>(
    () => loadFromStorage(),
  );
  const capture = usePostHogCapture();

  /**
   * Check if a matter is favorited
   */
  const isFavorited = useCallback(
    (matterId: string): boolean => {
      return matterId in favoritedMatters;
    },
    [favoritedMatters],
  );

  /**
   * Toggle the favorite status of a matter
   */
  const toggleFavorite = useCallback(
    (matterId: string, matterName: string) => {
      setFavoritedMatters((prev) => {
        const wasFavorited = matterId in prev;
        let next: FavoritedMattersMap;

        if (wasFavorited) {
          // Remove from favorites
          const { [matterId]: _, ...rest } = prev;
          next = rest;
        } else {
          // Check limit
          if (Object.keys(prev).length >= MAX_FAVORITED_MATTERS) {
            console.warn('Maximum favorited matters limit reached');
            return prev;
          }

          // Add to favorites
          next = {
            ...prev,
            [matterId]: {
              matterId,
              matterName,
              favoritedAt: new Date().toISOString(),
            },
          };
        }

        saveToStorage(next);

        // Track analytics
        capture(wasFavorited ? 'matter_unfavorited' : 'matter_favorited', {
          page: 'matters',
          matter_id: matterId,
          matter_name: matterName,
          favorited_count: Object.keys(next).length,
        });

        return next;
      });
    },
    [capture],
  );

  /**
   * Add a matter to favorites
   */
  const addFavorite = useCallback((matterId: string, matterName: string) => {
    setFavoritedMatters((prev) => {
      if (matterId in prev) return prev;

      if (Object.keys(prev).length >= MAX_FAVORITED_MATTERS) {
        console.warn('Maximum favorited matters limit reached');
        return prev;
      }

      const next = {
        ...prev,
        [matterId]: {
          matterId,
          matterName,
          favoritedAt: new Date().toISOString(),
        },
      };
      saveToStorage(next);
      return next;
    });
  }, []);

  /**
   * Remove a matter from favorites
   */
  const removeFavorite = useCallback((matterId: string) => {
    setFavoritedMatters((prev) => {
      if (!(matterId in prev)) return prev;

      const { [matterId]: _, ...next } = prev;
      saveToStorage(next);
      return next;
    });
  }, []);

  /**
   * Get all favorited matter IDs
   */
  const getFavoritedIds = useCallback((): Set<string> => {
    return new Set(Object.keys(favoritedMatters));
  }, [favoritedMatters]);

  /**
   * Get all favorited matters with their data
   */
  const getAllFavorites = useCallback((): FavoritedMatterData[] => {
    return Object.values(favoritedMatters).sort(
      (a, b) =>
        new Date(b.favoritedAt).getTime() - new Date(a.favoritedAt).getTime(),
    );
  }, [favoritedMatters]);

  /**
   * Clear all favorites
   */
  const clearAllFavorites = useCallback(() => {
    setFavoritedMatters({});
    saveToStorage({});
  }, []);

  return {
    favoritedMatters,
    favoritedCount: Object.keys(favoritedMatters).length,
    isFavorited,
    toggleFavorite,
    addFavorite,
    removeFavorite,
    getFavoritedIds,
    getAllFavorites,
    clearAllFavorites,
  };
};
